<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import {
  fetchTutorial, saveTutorialStep, completeTutorial, skipTutorial,
} from '../game/tutorial-api';
import cashIcon from '../assets/omerta/icons/cash.webp';
import goldBarsIcon from '../assets/omerta/icons/gold-bars.webp';
import nerveIcon from '../assets/omerta/icons/nerve.webp';

const emit = defineEmits<{ (e: 'done'): void; (e: 'navigate', view: string): void; (e: 'updated'): void }>();

/** The screen behind the card for each beat — null keeps whatever backdrop is
 *  already showing (used for beats that don't describe one specific screen). */
type TargetView = 'profile' | 'map' | 'districts' | 'market' | 'jobs' | 'chat'
  | 'rankings' | 'dungeons' | 'pit' | 'reserve' | 'counting' | 'settings';

interface Step { title: string; body: string; view: TargetView | null }

// 9 onboarding beats — teach the (restored) spatial dominion loop, each parked
// on the screen it's actually talking about so the player previews it live.
// {cash}/{gold}/{nerve} tokens render as icon+name pairs — see renderBody().
const STEPS: Step[] = [
  { view: null, title: 'Welcome to the City',
    body: 'The whole map is one city, carved into 8 mafia turfs. You start as a small-time crew — build your name.' },
  { view: 'map', title: 'Walk the streets',
    body: 'Open City Map and click a hex next to you to move. Movement costs {nerve} — the terrain sets the price (streets are cheap; highrise blocks & the river are impassable).' },
  { view: 'map', title: 'Fog of war',
    body: 'You only see what\'s around you. Move to reveal the map — explored ground stays dimly remembered.' },
  { view: 'map', title: 'Fight what\'s next to you',
    body: 'Enemies sit on tiles. Click one: your crew walks up and fights. You can only fight what you\'re standing on or beside.' },
  { view: 'market', title: 'Gear up',
    body: 'Wins drop loot — equip it, upgrade it (1→5), repair it when it wears (costs {cash}), or quick-sell it for scratch. Short on funds? Buy {gold} at the Store.' },
  { view: 'districts', title: 'Take turf',
    body: 'Reach a family\'s stronghold on the map and take their district. Owned turf pays passive {cash} — collect it in Districts.' },
  { view: 'pit', title: 'The Pit',
    body: 'Fight other outfits in The Pit for {cash} — win, and you steal 15% of theirs.' },
  { view: 'dungeons', title: 'Heists',
    body: 'Run Heists for staged scores and first-clear bonuses — {cash} and loot for a job well planned.' },
  { view: 'rankings', title: 'Climb the boards',
    body: 'Turfs taken, battles won, arsenal value — every season resets the map. Check Rankings to see where your crew stands. Make your bones. Good luck.' },
];

const CURRENCY = {
  cash: { icon: cashIcon, label: 'cash' },
  gold: { icon: goldBarsIcon, label: 'gold bars' },
  nerve: { icon: nerveIcon, label: 'nerve' },
} as const;
type CurrencyKey = keyof typeof CURRENCY;

/** Split a beat's body on {cash}/{gold}/{nerve} tokens so the template can
 *  drop in the real currency icon next to the word instead of bare text —
 *  QA: "no icons for each in-game currency... I don't understand it." */
function renderBody(body: string): Array<{ text: string } | { currency: CurrencyKey }> {
  const parts: Array<{ text: string } | { currency: CurrencyKey }> = [];
  const re = /\{(cash|gold|nerve)\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(body))) {
    if (m.index > last) parts.push({ text: body.slice(last, m.index) });
    parts.push({ currency: m[1] as CurrencyKey });
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push({ text: body.slice(last) });
  return parts;
}

const state = ref<{ step: number; completed: boolean } | null>(null);
// `idx` is what's on screen right now (Back can move it down again); `furthest`
// is the highest beat actually walked — that's the only thing ever reported to
// the server, since progress() forbids jumping ahead but happily accepts a
// client that revisits earlier beats without re-posting them.
const idx = ref(0);
const furthest = ref(0);
const busy = ref(false);
const rewardMsg = ref('');

const current = computed(() => STEPS[Math.min(idx.value, STEPS.length - 1)]);
const currentParts = computed(() => renderBody(current.value.body));
const isLast = computed(() => idx.value >= STEPS.length - 1);
const showMoveDemo = computed(() => idx.value === 1);

function emitNav() {
  const view = current.value.view;
  if (view) emit('navigate', view);
}

onMounted(async () => {
  try {
    const s = await fetchTutorial();
    state.value = { step: s.step, completed: s.completed };
    idx.value = furthest.value = Math.min(s.step, STEPS.length - 1);
    if (s.completed) emit('done');
    else emitNav();
  } catch { emit('done'); } // non-fatal: never block the game on onboarding
});

function back() {
  if (busy.value || idx.value === 0) return;
  idx.value -= 1;
  emitNav();
}

async function next() {
  if (busy.value) return;
  busy.value = true;
  try {
    if (isLast.value) {
      const res = await completeTutorial();
      if (res.reward) {
        rewardMsg.value = `+${res.reward.cash} cash · +${res.reward.xp} XP`;
        emit('updated'); // the reward was credited server-side — sync the chips
      }
      emit('navigate', 'profile');
      setTimeout(() => emit('done'), res.reward ? 1400 : 0);
      if (!res.reward) emit('done');
    } else {
      idx.value += 1;
      if (idx.value > furthest.value) {
        furthest.value = idx.value;
        void saveTutorialStep(idx.value);
      }
      emitNav();
    }
  } finally { busy.value = false; }
}

async function onSkip() {
  if (busy.value) return;
  busy.value = true;
  try { await skipTutorial(); } catch { /* ignore */ }
  emit('navigate', 'profile');
  emit('done');
}
</script>

<template>
  <!-- Docked to the side (dominion parity) rather than a full-screen scrim —
       the whole point of step 3's per-beat navigation is that the real screen
       (map/Pit/Districts/...) stays visible and legible behind the card. -->
  <div v-if="state && !state.completed" class="fixed inset-0 z-[200] pointer-events-none">
    <div class="tut-card pointer-events-auto rounded-lg border border-gold/40 bg-surface p-5" style="box-shadow: var(--shadow-lift)">
      <div class="flex items-center gap-2 mb-4">
        <span v-for="(s, i) in STEPS" :key="i"
              :class="['h-1.5 flex-1 rounded', i <= idx ? 'bg-gold' : 'bg-border']"></span>
      </div>

      <h2 class="font-heading text-2xl text-gold mb-2">{{ current.title }}</h2>
      <p class="font-body text-text mb-4 leading-relaxed">
        <template v-for="(p, i) in currentParts" :key="i"
        ><span v-if="'text' in p">{{ p.text }}</span
        ><span v-else class="curr-chip">
            <img :src="CURRENCY[p.currency].icon" :alt="CURRENCY[p.currency].label" class="curr-icon" />
            <span class="curr-label">{{ CURRENCY[p.currency].label }}</span>
          </span
        ></template>
      </p>

      <!-- Movement mini-preview — dominion parity: a small illustration beats
           prose for "click adjacent hex, pay nerve" (QA: "no preview of...
           how to move"). Purely decorative; the real map is live behind the card. -->
      <div v-if="showMoveDemo" class="move-demo" aria-hidden="true">
        <div class="move-hex move-hex--you"><span class="move-you-dot"></span></div>
        <svg class="move-arrow" viewBox="0 0 64 24" width="64" height="24">
          <path d="M2 12 H46" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" />
          <path d="M38 4 L48 12 L38 20" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
          <circle class="move-cursor" r="3.5" cy="12" />
        </svg>
        <div class="move-hex move-hex--target">
          <span class="move-cost">-1 <img :src="nerveIcon" alt="nerve" /></span>
        </div>
      </div>
      <p v-if="showMoveDemo" class="move-caption">Click a glowing hex next to you to walk there.</p>

      <p v-if="rewardMsg" class="font-label uppercase tracking-wider text-sm text-gold-bright mb-4">
        Reward: {{ rewardMsg }}
      </p>

      <div class="flex items-center justify-between mt-2 flex-wrap gap-y-2">
        <button
          :disabled="busy"
          @click="onSkip"
          class="btn-deco btn-deco--ghost btn-deco--sm shrink-0"
        >Skip</button>
        <div class="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
          <button
            v-if="idx > 0"
            :disabled="busy"
            @click="back"
            class="btn-deco btn-deco--ghost btn-deco--sm shrink-0"
          >Back</button>
          <span class="font-label uppercase tracking-wider text-xs text-text-dim shrink-0 whitespace-nowrap">{{ idx + 1 }} / {{ STEPS.length }}</span>
          <button
            :disabled="busy"
            @click="next"
            class="btn-deco btn-deco--gold shrink-0 whitespace-nowrap"
          >{{ isLast ? 'Make my bones' : 'Next' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Docked card: bottom sheet on narrow viewports, a right-hand rail (Burgaz's
   spot in dominion) from sm up — in both cases most of the screen behind it
   (the map, the Pit, Districts...) stays visible so the step reads as a live
   preview, not a blackout modal. */
.tut-card {
  position: absolute;
  left: 12px; right: 12px; bottom: 12px;
  max-height: min(74vh, 640px);
  overflow-y: auto;
}
@media (min-width: 640px) {
  .tut-card {
    left: auto; right: 20px; bottom: auto;
    top: 50%; transform: translateY(-50%);
    width: 380px; max-height: min(80vh, 600px);
  }
}

/* Currency icon+name pair, inline with the beat's prose. */
.curr-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 7px 1px 3px; margin: 0 1px;
  border-radius: 999px;
  background: rgba(200, 162, 76, 0.12);
  vertical-align: -4px;
}
.curr-icon { width: 18px; height: 18px; object-fit: contain; }
.curr-label {
  font-family: var(--font-label, monospace);
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
  color: var(--color-gold-bright);
  text-transform: none;
}

/* Movement mini-demo: a "you" hex, an arrow, a target hex tagged with its
   nerve cost — a tiny animated pointer nudges along the arrow on a loop. */
.move-demo {
  display: flex; align-items: center; justify-content: center; gap: 4px;
  margin: 4px 0 8px;
}
.move-hex {
  position: relative; width: 46px; height: 40px; flex: none;
  clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  display: grid; place-items: center;
}
.move-hex--you {
  background: linear-gradient(180deg, rgba(200,162,76,0.35), rgba(200,162,76,0.12));
  box-shadow: inset 0 0 0 1.5px var(--color-gold);
}
.move-you-dot {
  width: 12px; height: 12px; border-radius: 50%;
  background: var(--color-gold-bright);
  box-shadow: 0 0 10px rgba(230, 195, 110, 0.8);
}
.move-hex--target {
  background: linear-gradient(180deg, rgba(120,200,150,0.22), rgba(120,200,150,0.08));
  box-shadow: inset 0 0 0 1.5px rgba(140, 210, 160, 0.7);
  animation: move-target-pulse 1.8s ease-in-out infinite;
}
.move-cost {
  display: inline-flex; align-items: center; gap: 2px;
  font-family: var(--font-label, monospace);
  font-size: 10px; font-weight: 700; color: var(--color-text);
}
.move-cost img { width: 13px; height: 13px; object-fit: contain; }
.move-arrow { color: var(--color-gold); flex: none; }
.move-cursor {
  fill: var(--color-gold-bright);
  animation: move-cursor-travel 1.8s ease-in-out infinite;
}
.move-caption {
  text-align: center; margin: -4px 0 14px;
  font-family: var(--font-label, monospace);
  font-size: 11px; letter-spacing: 0.06em;
  color: var(--color-text-dim);
}
@keyframes move-cursor-travel {
  0%, 15% { transform: translateX(0); opacity: 0; }
  25% { opacity: 1; }
  70% { transform: translateX(34px); opacity: 1; }
  85%, 100% { transform: translateX(34px); opacity: 0; }
}
@keyframes move-target-pulse {
  0%, 100% { box-shadow: inset 0 0 0 1.5px rgba(140, 210, 160, 0.7); }
  50% { box-shadow: inset 0 0 0 1.5px rgba(140, 210, 160, 1), 0 0 14px rgba(140, 210, 160, 0.45); }
}
@media (prefers-reduced-motion: reduce) {
  .move-cursor, .move-hex--target { animation: none; }
  .move-cursor { opacity: 1; transform: translateX(17px); }
}
</style>
